'use client';

import React, { forwardRef, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
    useDisclosure,
    Drawer,
    DrawerContent,
    DrawerCloseButton,
    DrawerBody,
    Icons,
    Flex,
} from '@shared/ui';
import { useRouter, useSearchParams } from 'next/navigation';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { TRACK_EVENT, logEvent } from 'app/libs/track';
import { useBuyerBrandStore } from 'app/store/brand';
import i18n from '@shared/i18n';
import { PLAN_LOCK_FEATURE, checkPlanRestriction } from 'app/plan';
import { SearchInput } from './SearchInput';
import { SearchCombo } from './SearchCombo';
import { SearchBarButton } from './SearchBarButton';
import { searchBarTypeKey } from './define';
import './SearchBar.scss';

interface SearchBarProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    // 自行加入其他 props
    content: string;
}

export const SearchBar = forwardRef<HTMLDivElement, SearchBarProps>((props, ref) => {
    const searchParams = useSearchParams();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const [searchKeyword, setSearchKeyword] = useState(searchParams.get('search') || '');

    const { isEditingMode, style, content, styleMap } = props;

    const router = useRouter();

    const { plan = null } = useBuyerBrandStore();

    const type = styleMap?.[FEATURE_FLAG_KEYS.SEARCH_BOX_TYPE];

    const handleSearch = () => {
        logEvent(TRACK_EVENT.SEARCH, {
            event: TRACK_EVENT.SEARCH,
        });

        router.push(`/browse?search=${searchKeyword}`);

        onClose();
    };

    const searchBarPermission = useMemo(() => {
        if (!plan && isEditingMode) {
            return {
                enabled: true,
            };
        }

        if (!plan) {
            return {
                enabled: false,
            };
        }

        return checkPlanRestriction(plan, PLAN_LOCK_FEATURE.COMPONENT_SEARCH_BAR);
    }, [plan, isEditingMode]);

    if (!searchBarPermission.enabled) {
        return null;
    }

    const useTypingSuggestion = useMemo(() => {
        if (!plan) {
            return false;
        }

        const planDetail = checkPlanRestriction(
            plan,
            PLAN_LOCK_FEATURE.COMPONENT_TYPING_SUGGESTION
        );

        return planDetail.enabled;
    }, [plan]);

    return (
        <div
            ref={ref}
            style={style}
            className={clsx(
                'search-bar',
                {
                    'is-editing': isEditingMode,
                },
                type
            )}
        >
            <SearchBarButton
                searchKeyword={searchKeyword}
                onClick={handleSearch}
                isOpen={isOpen}
                onOpen={onOpen}
            />
            {type !== searchBarTypeKey.style4 &&
                (useTypingSuggestion ? (
                    <SearchCombo
                        name="search"
                        className="search-bar-input--desktop"
                        defaultValue={searchKeyword}
                        placeholder={content}
                        onChange={(value) => setSearchKeyword(value)}
                        onEnter={handleSearch}
                    />
                ) : (
                    <SearchInput
                        name="search"
                        className="search-bar-input--desktop"
                        defaultValue={searchKeyword}
                        placeholder={content}
                        onChange={(value) => setSearchKeyword(value)}
                        onEnter={handleSearch}
                    />
                ))}
            <Drawer onClose={onClose} isOpen={isOpen} placement="top" size="full">
                <DrawerContent borderRadius="0">
                    <DrawerCloseButton />
                    <DrawerBody px="32px" pt="64px">
                        {useTypingSuggestion ? (
                            <SearchCombo
                                className="search-bar-input--drawer"
                                name="search"
                                defaultValue={searchKeyword}
                                placeholder={content}
                                onChange={(value) => setSearchKeyword(value)}
                            />
                        ) : (
                            <SearchInput
                                className="search-bar-input--drawer"
                                name="search"
                                defaultValue={searchKeyword}
                                placeholder={content}
                                onChange={(value) => setSearchKeyword(value)}
                            />
                        )}
                        <Flex
                            justifyContent="center"
                            alignItems="center"
                            gap="8px"
                            color="neutral.130"
                            fontSize="14px"
                            fontWeight="500"
                            cursor="pointer"
                            p="8px"
                            mt="24px"
                            borderRadius="8px"
                            border="1px solid"
                            borderColor="neutral.020"
                            onClick={handleSearch}
                        >
                            <Icons.csr.MagnifyingGlass
                                className="search-bar-icon"
                                width="16px"
                                height="16px"
                                weight="bold"
                            />
                            {i18n.gettext('搜尋')}
                        </Flex>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </div>
    );
});

SearchBar.displayName = 'SearchBar';
